// Discount Rule service — PAGES.md §3.91
// Configure and manage all discount types: item-level, bill-level,
// promotional, coupon, and role-based limits.
// Vertical: SHARED. No feature flag.

import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type RuleType = 'ITEM_LEVEL' | 'BILL_LEVEL' | 'PROMOTIONAL' | 'COUPON';
export type DiscountType = 'percentage' | 'flat';
export type RuleStatus = 'ACTIVE' | 'SCHEDULED' | 'EXPIRED' | 'DISABLED';
export type PromoType = 'percentage_off' | 'flat_off' | 'buy_x_get_y' | 'combo_price';
export type CustomerSegment = 'ALL' | 'NEW' | 'LOYAL' | 'CORPORATE';

export interface DiscountRule {
  id: string;
  tenant_id: string;
  name: string;
  rule_type: RuleType;
  discount_type: DiscountType;
  value: number;          // percentage (0-100) or paise
  min_bill_amount: number; // paise, 0 = no minimum
  min_quantity: number | null;
  customer_segment: CustomerSegment | null;
  applicable_product_ids: string[];
  applicable_category_ids: string[];
  status: RuleStatus;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null; // HH:MM (24h), local tenant TZ
  end_time: string | null;   // HH:MM
  days_of_week: number[];    // 0=Sun..6=Sat; empty = every day
  requires_approval: boolean;
  stackable: boolean;
  is_active: boolean;
  promo_type: PromoType | null;
  buy_quantity: number | null;
  get_quantity: number | null;
  combo_product_ids: string[];
  combo_price: number | null; // paise
  combines_with_product_discounts: boolean;
  combines_with_bill_discounts: boolean;
  /** Stores the rule is for; empty = every store (C10). */
  location_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface CreateDiscountRuleDto {
  name: string;
  rule_type: RuleType;
  discount_type: DiscountType;
  value: number;
  min_bill_amount?: number;
  min_quantity?: number;
  customer_segment?: CustomerSegment;
  applicable_product_ids?: string[];
  applicable_category_ids?: string[];
  start_date?: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  days_of_week?: number[];
  requires_approval?: boolean;
  stackable?: boolean;
  is_active?: boolean;
  promo_type?: PromoType;
  buy_quantity?: number;
  get_quantity?: number;
  combo_product_ids?: string[];
  combo_price?: number;
  combines_with_product_discounts?: boolean;
  combines_with_bill_discounts?: boolean;
  /** [] = every store; on update, omitted = unchanged (C10). */
  location_ids?: string[];
}

export interface RoleLimitConfig {
  role: string;
  max_discount_percentage: number;
}

export interface DiscountConfig {
  role_limits: RoleLimitConfig[];
  owner_override_allowed: boolean;
  reason_capture_mandatory: boolean;
}

export interface DiscountRuleListParams {
  page?: number;
  limit?: number;
  type?: string;
  status?: string;
  search?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const discountRuleService = {
  async list(params: DiscountRuleListParams = {}): Promise<{
    data: DiscountRule[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      page: params.page,
      limit: params.limit ?? 20,
      type: params.type,
      status: params.status,
      search: params.search,
    });
    return apiClientPaginated<DiscountRule[]>(`/discounts/rules${q}`);
  },

  async create(dto: CreateDiscountRuleDto): Promise<DiscountRule> {
    return apiClient<DiscountRule>('/discounts/rules', { method: 'POST', body: dto });
  },

  async update(id: string, dto: Partial<CreateDiscountRuleDto> & { is_active?: boolean }): Promise<DiscountRule> {
    return apiClient<DiscountRule>(`/discounts/rules/${id}`, { method: 'PUT', body: dto });
  },

  async delete(id: string): Promise<void> {
    await apiClient<void>(`/discounts/rules/${id}`, { method: 'DELETE' });
  },

  async getConfig(): Promise<DiscountConfig> {
    return apiClient<DiscountConfig>('/discounts/config');
  },

  async updateConfig(config: DiscountConfig): Promise<DiscountConfig> {
    return apiClient<DiscountConfig>('/discounts/config', { method: 'PUT', body: config });
  },
};

export { ApiError };
